// Wochenraster der Ein-/Ausschaltzeiten: ein Fenster je Wochentag (einfaches Raster) ↔ API-Fenster.
// Regeln wie packages/schedule/src/power.ts: off ≤ on = über Mitternacht (zählt zum Starttag), "00:00"–"00:00" = ganzer Tag.
import { WEEKDAYS, type PowerWindow, type Weekday } from '../api/emergency-power';

export interface DayWindow { on: string; off: string }
export type WeekGrid = Record<Weekday, DayWindow | null>;

export const minutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** Erstes Fenster je Tag (das einfache Raster kennt ein Fenster pro Tag). */
export function toGrid(weekly: readonly PowerWindow[]): WeekGrid {
  const g = Object.fromEntries(WEEKDAYS.map((d) => [d, null])) as WeekGrid;
  for (const w of weekly) for (const d of w.weekdays) g[d] ??= { on: w.on, off: w.off };
  return g;
}

/** Raster → API-Fenster; Tage mit gleichen Zeiten werden zusammengefasst. */
export function fromGrid(g: WeekGrid): PowerWindow[] {
  const out: PowerWindow[] = [];
  for (const d of WEEKDAYS) {
    const w = g[d];
    if (!w) continue;
    const same = out.find((x) => x.on === w.on && x.off === w.off);
    if (same) same.weekdays.push(d);
    else out.push({ weekdays: [d], on: w.on, off: w.off });
  }
  return out;
}

/** Hat der Zeitplan mehr als ein Fenster an einem Tag? (dann zeigt das Raster nur das erste) */
export function hasMultiple(weekly: readonly PowerWindow[]): boolean {
  const seen = new Set<Weekday>();
  for (const w of weekly) for (const d of w.weekdays) {
    if (seen.has(d)) return true;
    seen.add(d);
  }
  return false;
}

/** Eingeschaltete Abschnitte eines Tages in Minuten [von, bis), inkl. Übertrag vom Vortag über Mitternacht. */
export function daySegments(weekly: readonly PowerWindow[], day: Weekday): [number, number][] {
  const idx = WEEKDAYS.indexOf(day);
  const prev = WEEKDAYS[(idx + 6) % 7]!;
  const segs: [number, number][] = [];
  for (const w of weekly) {
    const on = minutes(w.on);
    const off = w.off === '24:00' ? 1440 : minutes(w.off);
    const overnight = off <= on;
    if (w.weekdays.includes(day)) segs.push([on, overnight ? 1440 : off]);
    if (overnight && off > 0 && w.weekdays.includes(prev)) segs.push([0, off]);
  }
  segs.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const s of segs) {
    const last = merged[merged.length - 1];
    if (last && s[0] <= last[1]) last[1] = Math.max(last[1], s[1]);
    else merged.push([...s]);
  }
  return merged;
}

/** Stunden pro Woche eingeschaltet (Hinweis im Editor). */
export const weeklyHours = (weekly: readonly PowerWindow[]): number =>
  WEEKDAYS.reduce((sum, d) => sum + daySegments(weekly, d).reduce((s, [a, b]) => s + (b - a), 0), 0) / 60;
